import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CreatePaymentIntentRequest {
  amount: number; // Amount in cents (e.g., 2500 = $25.00)
  currency: string; // e.g., 'usd', 'eur'
  activityId?: string;
  bookingId?: string;
  metadata?: {
    [key: string]: string;
  };
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  status: string;
}

export interface ConfirmPaymentIntentRequest {
  paymentIntentId: string;
  paymentMethodId: string;
}

export interface ConfirmPaymentIntentResponse {
  id: string;
  status: string;
  amount: number;
  currency: string;
}

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  // Stripe API URL (for reference only - direct API calls should go through backend)
  private readonly apiUrl = environment.stripeApiUrl || 'https://api.stripe.com/v1';
  
  // IMPORTANT: The Stripe Secret Key is stored securely in AWS Secrets Manager
  // It is NEVER exposed in frontend code or committed to version control

  constructor(private http: HttpClient) {}

  /**
   * Get the Stripe function URL from amplify_outputs.json or environment
   * In Amplify Gen 2, functions are exposed under the 'functions' property
   */
  private async getStripeFunctionUrl(): Promise<string> {
    try {
      // First, check environment variable (for manual override during development)
      if (environment.stripeFunctionUrl) {
        console.log('[STRIPE SERVICE] Using function URL from environment:', environment.stripeFunctionUrl);
        return environment.stripeFunctionUrl;
      }

      // Try to load from amplify_outputs.json
      const outputs: any = await import('../../../amplify_outputs.json');
      
      // Amplify Gen 2 structure: outputs.functions.stripeFunction.urls.<environment>
      if (outputs?.functions?.stripeFunction?.urls) {
        // Get the first available URL (usually for the current environment)
        const urls = outputs.functions.stripeFunction.urls;
        const urlKeys = Object.keys(urls);
        if (urlKeys.length > 0) {
          const url = urls[urlKeys[0]];
          console.log('[STRIPE SERVICE] Found function URL in amplify_outputs.json:', url);
          return url;
        }
      }
      
      // Alternative structure: outputs.functions.stripeFunction.url
      if (outputs?.functions?.stripeFunction?.url) {
        console.log('[STRIPE SERVICE] Found function URL (alternative structure):', outputs.functions.stripeFunction.url);
        return outputs.functions.stripeFunction.url;
      }
      
      // Check for custom endpoints or functions (legacy structure)
      if (outputs?.default?.custom?.stripeFunctionUrl) {
        return outputs.default.custom.stripeFunctionUrl;
      }
      
      if (outputs?.custom?.stripeFunctionUrl) {
        return outputs.custom.stripeFunctionUrl;
      }
      
      // Log the actual structure for debugging
      console.warn('[STRIPE SERVICE] Stripe function URL not found. Available structure:', {
        hasFunctions: !!outputs?.functions,
        functionKeys: outputs?.functions ? Object.keys(outputs.functions) : [],
        hasCustom: !!outputs?.custom,
        customKeys: outputs?.custom ? Object.keys(outputs.custom) : []
      });
      
      throw new Error('Stripe function URL not configured. Please deploy the Amplify function first or set stripeFunctionUrl in environment.ts');
    } catch (error) {
      if (error instanceof Error && error.message.includes('not configured')) {
        throw error;
      }
      console.error('[STRIPE SERVICE] Error loading amplify_outputs.json:', error);
      throw new Error('Stripe function URL not configured. Please deploy the Amplify function first or set stripeFunctionUrl in environment.ts');
    }
  }

  /**
   * Create a payment intent on the backend (Amplify function)
   * This calls the Amplify Lambda function which uses the Stripe secret key
   */
  createPaymentIntent(request: CreatePaymentIntentRequest): Observable<CreatePaymentIntentResponse> {
    return from(this.getStripeFunctionUrl()).pipe(
      switchMap((functionUrl) => {
        // Remove trailing slash if present to avoid double slashes
        const baseUrl = functionUrl.endsWith('/') ? functionUrl.slice(0, -1) : functionUrl;
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        return this.http.post<CreatePaymentIntentResponse>(
          `${baseUrl}/create-payment-intent`,
          request,
          { headers }
        ).pipe(
          map(response => ({
            clientSecret: response.clientSecret,
            paymentIntentId: response.paymentIntentId,
            status: response.status
          })),
          catchError(error => {
            console.error('[STRIPE SERVICE] Error creating payment intent:', error);
            return throwError(() => new Error(`Failed to create payment intent: ${error.message || 'Unknown error'}`));
          })
        );
      }),
      catchError(error => {
        // Re-throw the error from getStripeFunctionUrl
        return throwError(() => error);
      })
    );
  }

  /**
   * Confirm a payment intent on the backend (Amplify function)
   * This calls the Amplify Lambda function which uses the Stripe secret key
   */
  confirmPaymentIntent(request: ConfirmPaymentIntentRequest): Observable<ConfirmPaymentIntentResponse> {
    return from(this.getStripeFunctionUrl()).pipe(
      switchMap((functionUrl) => {
        // Remove trailing slash if present to avoid double slashes
        const baseUrl = functionUrl.endsWith('/') ? functionUrl.slice(0, -1) : functionUrl;
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        return this.http.post<ConfirmPaymentIntentResponse>(
          `${baseUrl}/confirm-payment-intent`,
          request,
          { headers }
        ).pipe(
          map(response => ({
            id: response.id,
            status: response.status,
            amount: response.amount,
            currency: response.currency
          })),
          catchError(error => {
            console.error('[STRIPE SERVICE] Error confirming payment intent:', error);
            return throwError(() => new Error(`Failed to confirm payment intent: ${error.message || 'Unknown error'}`));
          })
        );
      }),
      catchError(error => {
        // Re-throw the error from getStripeFunctionUrl
        return throwError(() => error);
      })
    );
  }

  /**
   * Get payment intent status from Amplify function
   */
  getPaymentIntentStatus(paymentIntentId: string): Observable<any> {
    return from(this.getStripeFunctionUrl()).pipe(
      switchMap((functionUrl) => {
        // Remove trailing slash if present to avoid double slashes
        const baseUrl = functionUrl.endsWith('/') ? functionUrl.slice(0, -1) : functionUrl;
        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        return this.http.get<any>(
          `${baseUrl}/payment-intent/${paymentIntentId}`,
          { headers }
        ).pipe(
          catchError(error => {
            console.error('[STRIPE SERVICE] Error getting payment intent status:', error);
            return throwError(() => new Error(`Failed to get payment intent status: ${error.message || 'Unknown error'}`));
          })
        );
      }),
      catchError(error => {
        // Re-throw the error from getStripeFunctionUrl
        return throwError(() => error);
      })
    );
  }
}

